import React, { useState, useRef, useEffect, useMemo } from "react";
import Button from "../../elements/buttons/Button";
import "./chatBot.css";
import useStore from "../../../useStore";

const hasHTML = (text = "") => /<[^>]+>/.test(text);
const stripHTML = (html = "") => {
  const tmp = document.createElement("div");
  tmp.innerHTML = html;
  return (tmp.textContent || tmp.innerText || "").trim();
};

const urlRegex = /(https?:\/\/[^\s<]+)/gi;
const escapeHtml = (str = "") =>
  str.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

const linkify = (text = "") =>
  escapeHtml(text).replace(
    urlRegex,
    (u) => `<a href="${u}" target="_blank" rel="noopener noreferrer">${u}</a>`
  );

const smartLinkify = (raw = "") => {
  const lines = raw.split(/\n/);
  const htmlLines = lines.map((line) => {
    const original = line;

    const m = original.match(
      /^\s*(?:•|\-)?\s*(.+?)\s*(?:→|:)\s*(https?:\/\/[^\s<]+)\s*$/i
    );

    if (m) {
      const name = m[1].trim();
      const url = m[2].trim();
      const hadBullet = /^\s*•/.test(original);

      const safeName = escapeHtml(name);
      const anchor = `<a href="${url}" target="_blank" rel="noopener noreferrer">${safeName}</a>`;

      return `${hadBullet ? "• " : ""}${anchor}`;
    }

    return linkify(original);
  });

  return htmlLines.join("<br/>");
};

const hasURL = (text = "") => urlRegex.test(text);

const calcCharDelay = (text, maxMs = 5000, minDelay = 8, maxDelay = 35) => {
  const len = Math.max(1, text.length);
  const delay = Math.floor(maxMs / len);
  return Math.min(maxDelay, Math.max(minDelay, delay));
};

export default function ChatBot({ onClose }) {
  const [input, setInput] = useState("");
  const [showTypingIndicator, setShowTypingIndicator] = useState(false);
  const messagesEndRef = useRef(null);

  const { chatBot, chatLoading, error, sendChatMessage } = useStore();

  const initialMsgs = useMemo(
    () => [{ role: "bot", text: "¡Hola! 🤖 Soy tu asistente virtual de Quality Seal. ¿En qué puedo apoyarte hoy?" }],
    []
  );

  const history = useMemo(() => [...initialMsgs, ...chatBot], [initialMsgs, chatBot]);

  const [renderedHistory, setRenderedHistory] = useState(history);

  useEffect(() => {
    const last = history[history.length - 1];
    const prev = renderedHistory[renderedHistory.length - 1];

    if (!prev || history.length > renderedHistory.length) {
      const justPushed = last;

      if (justPushed?.role === "bot" && typeof justPushed.text === "string") {
        const rawAnswer = justPushed.text;
        const wasHTML = hasHTML(rawAnswer);
        const displayText = wasHTML ? stripHTML(rawAnswer) : rawAnswer;

        setRenderedHistory([...history.slice(0, -1), { ...justPushed, text: "" }]);

        setShowTypingIndicator(true);
        const speed = calcCharDelay(displayText, 5000);
        let i = 0;
        const interval = setInterval(() => {
          i++;
          const chunk = displayText.slice(0, i);
          setRenderedHistory((prevList) => {
            const copy = [...prevList];
            copy[copy.length - 1] = { ...copy[copy.length - 1], text: chunk, _renderHtml: false };
            return copy;
          });

          messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });

          if (i >= displayText.length) {
            clearInterval(interval);

            setRenderedHistory((prevList) => {
              const copy = [...prevList];
              const idx = copy.length - 1;
              const containsUrl = hasURL(rawAnswer);
              copy[idx] = {
                ...copy[idx],
                text: wasHTML
                  ? rawAnswer
                  : (containsUrl ? smartLinkify(rawAnswer) : displayText),
                _renderHtml: wasHTML || containsUrl
              };
              return copy;
            });

            setShowTypingIndicator(false);
          }
        }, speed);

      } else {
        setRenderedHistory(history);
      }
    } else {
      setRenderedHistory(history);
    }
  }, [history, renderedHistory]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [renderedHistory, showTypingIndicator, chatLoading]);

  const onSend = async () => {
    const msg = input.trim();
    if (!msg) return;
    setInput("");
    await sendChatMessage(msg);
  };

  return (
    <div className="chatbot-window">
      <div className="chatbot-header">
        <span>🤖 ChatBot</span>
        <Button text="✖" action={onClose} color="red" fill classNames="chatbot-close" />
      </div>

      <div className="chatbot-messages">
        {renderedHistory.map((m, i) => (
          <div key={i} className={`chatbot-message ${m.role === "user" ? "user" : "bot"}`}>
            <b>{m.role === "user" ? "Tú" : "Bot"}:</b>{" "}
            {m._renderHtml || hasURL(m.text) ? (
              <span
                dangerouslySetInnerHTML={{
                  __html: m._renderHtml ? m.text : smartLinkify(m.text)
                }}
              />
            ) : (
              <span>{m.text}</span>
            )}
          </div>
        ))}

        {(showTypingIndicator || chatLoading) && (
          <div className="typing-indicator">
            <div className="typing-logo">🤖</div>
            <div className="typing-dots">
              <i></i><i></i><i></i>
            </div>
          </div>
        )}

        {error && (
          <div className="chatbot-message bot" style={{ color: "crimson" }}>
            <b>Bot:</b> Ocurrió un error. Intenta de nuevo más tarde.
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      <div className="chatbot-input-row">
        <input
          className="chatbot-input"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && onSend()}
          placeholder="Escribe tu mensaje…"
        />
        <Button text="Enviar" action={onSend} color="blue" fill />
      </div>
    </div>
  );
}